import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TextField, Spinner } from "@shopify/polaris";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { CustomField } from "types/CustomField";
import { Product } from "types/Product";
import DatePickerx from "features/datepicker/date-picker";
import { useAppDispatch } from "hooks";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetchAuthorizedFileDetails } from "containers/file-authorized/authorized-file.Slice";
import { fetchById } from "services/upload-file.service";
import { fetchUserDetails } from "containers/user/user-container.slice";
import { fetchCategoryDetails } from "containers/document/category-tree/category-tree.Slice";
import { fetchFuelReportDetails } from "containers/fuel-report-view-container/fuel-report-container.slice";
import FileTemplateSelect from "components/file-template-select/file-template-select";
import {
  existingFuelReportDocumentId,
  fuelReportErrorMessage,
  isFuelReportDocumentAlreadyCreated,
} from "utils/fuel-report-errors";
import { useSignerSelection } from "../hooks/useSignerSelection";
import { useCustomFields } from "../hooks/useCustomFields";
import CustomFieldsRenderer from "../components/CustomFieldsRenderer";
import DocumentFormShell from "../components/DocumentFormShell";
import {
  validateForm,
  getParentTemplateMismatchErrorKey,
  buildPayload,
  BuildPdfFormProps,
  normalizeBuildPdfTemplateId,
} from "../build-pdf-utils";
import {
  submitDocument,
  updateDocument,
  fetchSystemTemplateFields,
} from "../build-pdf-api";

export default function FuelReportDocumentForm({
  customerId,
  isCustomerApproved = false,
  template,
  fileId,
  assignedUserId,
  id,
  subject,
  user,
  signType: signTypeProps,
  managerSigner,
  fuelReport,
}: BuildPdfFormProps) {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const dispatch = useAppDispatch();

  const [templateId, setTemplateId] = useState("");
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [productGroups, setProductGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [fileTypeContext, setFileTypeContext] = useState("");
  const [templateIdByFetch, setTemplateIdByFetch] = useState("");
  const [fuelReportId, setFuelReportId] = useState<any>(null);

  const [fuelSystemTemplateSource, setFuelSystemTemplateSource] = useState<
    string | null
  >(null);
  const [loadingSystemFields, setLoadingSystemFields] = useState(false);
  const [isEditingSystemTemplate, setIsEditingSystemTemplate] = useState(false);

  const isUsingFuelReportSlot = !id && fuelSystemTemplateSource === "system";

  const {
    selectedUser,
    selectedContactIds,
    signType,
    setSignType,
    selectedNonTechUser,
    selectedTabUser,
    hydrateSigners,
  } = useSignerSelection({
    signTypeProps,
    managerSigner,
    assignedUserId,
    isEditMode: !!id,
  });

  const {
    customFields,
    setCustomFields,
    templateEntity,
    setTemplateEntity,
    fileEntitiesMap,
    handleCustomFieldChange,
    transformCustomFields,
    setInitialCustomFieldsWhenEditLoaded,
    setInitialTemplateEntityWhenEditLoaded,
  } = useCustomFields({
    companyId,
    templateId,
    templateIdByFetch,
    isEditMode: !!id,
    onSignTypeDetected: setSignType,
  });

  useEffect(() => {
    if (fuelReport) setFuelReportId(fuelReport);
  }, [fuelReport]);

  useEffect(() => {
    if (!companyId || id) return;
    let cancelled = false;
    setLoadingSystemFields(true);
    fetchSystemTemplateFields(companyId!, "fuel_report")
      .then((data) => {
        if (cancelled) return;
        const fields: CustomField[] = data?.fields || [];
        const source = data?.source === "custom" ? "custom" : "system";
        setFuelSystemTemplateSource(source);
        if (source === "system" && fields.length > 0) {
          setCustomFields(
            fields.map((f: any) => ({
              ...f,
              value: f.isMulti ? [] : "",
            }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setFuelSystemTemplateSource("custom");
      })
      .finally(() => {
        if (!cancelled) setLoadingSystemFields(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, id, setCustomFields]);

  useEffect(() => {
    if (!id) return;
    async function fetchData() {
      try {
        const {
          date,
          templateId: templateId_,
          templateSource,
          signType,
          filesTemplateCustomField,
          fileTypeContext,
          signersList,
          fuelreport,
        } = await fetchById(id, companyId);

        const fetchedTemplateId = templateId_?.id;
        const isSys = templateSource === "system";
        setIsEditingSystemTemplate(isSys);
        const loadSystemTemplateFields = async () => {
          setIsEditingSystemTemplate(true);
          try {
            const data = await fetchSystemTemplateFields(
              companyId!,
              "fuel_report"
            );
            const sysFields: CustomField[] = data.fields || [];
            if (sysFields.length > 0) {
              const savedValues = filesTemplateCustomField?.customFields || {};
              setCustomFields(
                sysFields.map((f: any) => ({
                  ...f,
                  value: savedValues[f.label] ?? (f.isMulti ? [] : ""),
                }))
              );
            }
          } catch (_e) {
            // ignore
          }
        };

        if (fetchedTemplateId) {
          const fileTemplateEntity = await fetchFileTemplateById(
            companyId!,
            fetchedTemplateId
          );
          setTemplateEntity(fileTemplateEntity);
          if (isSys) {
            setTemplateId("");
            setTemplateIdByFetch("");
            await loadSystemTemplateFields();
          } else {
            setTemplateId(fetchedTemplateId);
            setTemplateIdByFetch(fetchedTemplateId);
            const transformedFields = await transformCustomFields(
              filesTemplateCustomField?.customFields,
              fileTemplateEntity.customFields
            );
            const loadedCustomFields = fileTemplateEntity.customFields.map(
              (field: CustomField) => ({
                ...field,
                value:
                  field.type === "file"
                    ? transformedFields[field.label] || []
                    : transformedFields[field.label] || "",
              })
            );
            setCustomFields(loadedCustomFields);
            setInitialCustomFieldsWhenEditLoaded(loadedCustomFields);
          }
          setInitialTemplateEntityWhenEditLoaded(fileTemplateEntity);
        } else if (isSys) {
          await loadSystemTemplateFields();
        }

        setProducts(filesTemplateCustomField?.products || []);
        setProductGroups(
          (filesTemplateCustomField as any)?.productGroups || []
        );
        setSignType(signType);
        setDate(date);
        setFileTypeContext(fileTypeContext);
        hydrateSigners(signersList);
        setFuelReportId(fuelreport?.id || null);
      } catch (_e) {
        // silently ignore
      }
    }
    fetchData();
  }, [id, companyId]);

  const parentTemplateId = !id ? normalizeBuildPdfTemplateId(template) : "";

  const isFormValid = () => {
    let validationErrors = validateForm({
      templateId,
      date,
      subject,
      selectedContactIds,
      signType,
      selectedUser,
      selectedNonTechUser,
      isSystemSource: isUsingFuelReportSlot,
      isEditingSystemTemplate,
      isAutoBusinessTripDocument: false,
      customFields,
      products,
      productGroups,
    });
    const parentMismatch = getParentTemplateMismatchErrorKey({
      templateId,
      parentTemplateId,
      isSystemSource: isUsingFuelReportSlot,
      isEditingSystemTemplate,
    });
    if (parentMismatch) {
      validationErrors = { ...validationErrors, templateId: parentMismatch };
    }
    const translatedErrors: { [key: string]: string } = {};
    for (const [key, val] of Object.entries(validationErrors)) {
      translatedErrors[key] =
        typeof val === "string" ? t(val) : t(val.key, val.params);
    }
    setErrors(translatedErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const closeModalSoon = () => {
    setTimeout(() => {
      dispatch(setIsModalxOpen(false));
    }, 1500);
  };

  const handleFormSubmit = async () => {
    if (!isFormValid()) return;
    const formData = buildPayload({
      date,
      templateId,
      customerId,
      customFields,
      products,
      productGroups,
      fileId,
      selectedContactIds,
      selectedUser,
      selectedNonTechUser,
      signType,
      selectedTabUser,
      fileTypeContext,
      user,
      orderId: null,
      fuelReportId,
      businessTripId: null,
      financeId: undefined,
      ...(isUsingFuelReportSlot && fuelReportId
        ? { fuelReportFileType: "fuel_report" }
        : {}),
      ...(isEditingSystemTemplate && fuelReportId
        ? { fuelReportFileType: "fuel_report" }
        : {}),
    });

    if (!companyId) return;
    try {
      setIsFailed(false);
      setFailureMessage(null);
      setIsLoading(true);
      const action = id ? updateDocument : submitDocument;
      const fileEntity = await action(companyId, formData, id);
      const savedFileId = fileEntity?.id ?? fileEntity?.data?.id;
      if (fileId) dispatch(fetchFileDetails({ fileId, companyId }));
      if (savedFileId) {
        if (user) dispatch(fetchUserDetails({ userId: user, companyId }));
        dispatch(fetchCategoryDetails({ companyId, source: "profile" }));
        dispatch(fetchFileDetails({ fileId: savedFileId, companyId }));
        dispatch(fetchAuthorizedFileDetails({ fileId: savedFileId, companyId }));
        if (companyId && fuelReportId) {
          dispatch(
            fetchFuelReportDetails({ companyId, reportId: fuelReportId })
          );
        }
        setIsLoading(false);
        setIsFailed(false);
        setIsSuccessful(true);
        closeModalSoon();
      } else {
        setIsFailed(true);
        setIsLoading(false);
      }
    } catch (e) {
      if (isFuelReportDocumentAlreadyCreated(e)) {
        // The report already has its document (an earlier save, maybe from
        // another tab or a double click): show it as done, not as a failure.
        const existingFileId = existingFuelReportDocumentId(e);
        if (existingFileId) {
          dispatch(fetchFileDetails({ fileId: existingFileId, companyId }));
        }
        if (fuelReportId) {
          dispatch(
            fetchFuelReportDetails({ companyId, reportId: fuelReportId })
          );
        }
        setIsLoading(false);
        setIsFailed(false);
        setSuccessMessage(t("fuel_report_document_already_created"));
        setIsSuccessful(true);
        closeModalSoon();
        return;
      }
      setIsFailed(true);
      setFailureMessage(fuelReportErrorMessage(e, t));
      setIsLoading(false);
    }
  };

  return (
    <DocumentFormShell
      onSubmit={handleFormSubmit}
      isLoading={isLoading}
      isFailed={isFailed}
      failureMessage={failureMessage}
      isSuccessful={isSuccessful}
      successMessage={successMessage}
      submitDisabled={loadingSystemFields}
    >
      {loadingSystemFields && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0",
          }}
        >
          <Spinner size="small" />
        </div>
      )}
      {!isUsingFuelReportSlot &&
        !(id && !templateId) &&
        !isEditingSystemTemplate &&
        !loadingSystemFields && (
          <>
            <FileTemplateSelect
              onChange={setTemplateId}
              value={templateId}
              isRequired={true}
              isCustomerApproved={isCustomerApproved}
              templateId={
                id
                  ? ""
                  : normalizeBuildPdfTemplateId(template) || templateIdByFetch
              }
              error={errors["templateId"]}
              subject={subject}
            />
            {templateId && (
              <TextField
                label={t("file_context")}
                type="text"
                autoComplete="off"
                value={fileTypeContext}
                onChange={setFileTypeContext}
                labelHidden
                placeholder={t("enter_file_context")}
              />
            )}
          </>
        )}
      <DatePickerx
        disableDatesAfter={null}
        title={t("document_date")}
        isRequired={true}
        onChange={(value: any) => setDate(value.toISOString().split("T")[0])}
        selected={date}
        readOnly={true}
      />
      <CustomFieldsRenderer
        customFields={customFields}
        errors={errors}
        onFieldChange={handleCustomFieldChange}
        products={products}
        productGroups={productGroups}
        setProducts={setProducts}
        setProductGroups={setProductGroups}
        orderId={null}
        templateId={templateId}
        customerId={customerId}
        fileEntitiesMap={fileEntitiesMap}
      />
    </DocumentFormShell>
  );
}
